import { Link } from "@tanstack/react-router";
import { ArrowUpRight, MousePointerClick, Zap } from "lucide-react";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { ListingThumb } from "@/components/listing-thumb";
import { ShareListingButton } from "@/components/share-buttons";
import type { BoardListing } from "@/components/listing-card";
import { badgesFor, formatIDR, levelFor } from "@/lib/board-constants";

export function ListingDetailSheet({
  listing,
  rank,
  onClose,
  onOpen,
  onOutbid,
}: {
  listing: BoardListing | null;
  rank: number;
  onClose: () => void;
  onOpen: (slug: string, url: string) => void;
  onOutbid: (listing: BoardListing) => void;
}) {
  return (
    <Sheet open={!!listing} onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full gap-0 overflow-y-auto sm:max-w-md">
        {listing && (
          <>
            <SheetHeader className="space-y-3 pb-3">
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <ListingThumb url={listing.url} name={listing.name} size={48} />
                  <div className="min-w-0">
                    <SheetTitle className="truncate font-display text-lg">{listing.name}</SheetTitle>
                    <SheetDescription className="truncate text-xs">
                      Peringkat #{rank} · {listing.category} · Lv{levelFor(listing.points).level}{" "}
                      {levelFor(listing.points).label}
                    </SheetDescription>
                  </div>
                </div>
                <ShareListingButton slug={listing.slug} name={listing.name} rank={rank} size="icon" variant="outline" />
              </div>
            </SheetHeader>

            <div className="space-y-4 px-4 pb-6">
              <p className="text-sm text-muted-foreground">{listing.description || "Tanpa deskripsi."}</p>

              <div className="grid grid-cols-3 gap-2">
                <Stat label="Total bid" value={listing.total_amount > 0 ? formatIDR(listing.total_amount) : "GRATIS"} />
                <Stat label="Klik" value={String(listing.clicks)} icon={<MousePointerClick className="size-3" />} />
                <Stat label="Poin" value={String(listing.points)} icon={<Zap className="size-3" />} />
              </div>

              {badgesFor(listing).length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {badgesFor(listing).map((b) => (
                    <span
                      key={b}
                      className="rounded-full bg-primary-soft px-2 py-0.5 text-[11px] font-medium text-accent-foreground"
                    >
                      {b}
                    </span>
                  ))}
                </div>
              )}

              <div className="grid gap-2">
                <button
                  onClick={() => onOpen(listing.slug, listing.url)}
                  className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-primary text-sm font-medium text-primary-foreground transition-opacity hover:opacity-90"
                >
                  Kunjungi listing <ArrowUpRight className="size-4" />
                </button>
                <button
                  onClick={() => onOutbid(listing)}
                  className="inline-flex h-10 items-center justify-center rounded-xl border border-border bg-background text-sm font-medium transition-colors hover:border-primary hover:bg-primary-soft hover:text-accent-foreground"
                >
                  Rebut peringkat ini
                </button>
                <Link
                  to="/produk/$slug"
                  params={{ slug: listing.slug }}
                  className="inline-flex h-10 items-center justify-center rounded-xl text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                >
                  Lihat detail & grafik performa
                </Link>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Stat({ label, value, icon }: { label: string; value: string; icon?: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-card p-2.5">
      <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
        {icon}
        {label}
      </span>
      <span className="mt-1 block truncate font-display text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}
